import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Table from '@/components/TableComponents/Table';
import { SearchIcon } from '../svg';
import AddUserModal from '../components/userManagement/AddUserModal';
import { toast } from 'react-toastify';
import { UserContext } from '../context/UserContext';
import { capitalizeFirstLetter } from '../utils/caseUtils';
import { BrandMemberRole } from '../types/enums';
import { Button } from '../components/common/Button';
import useI18n from '../hooks/useI18n';
import IconButton from '@/components/common/IconButton';
import ActionsPopup from '@/components/userManagement/ActionsPopup';
import EditUserRoleModal from '@/components/userManagement/EditUserRoleModal';
import DeactivateUserModel from '@/components/userManagement/DeactivateUserModal';
import DeleteUserModal from '@/components/userManagement/DeleteUserModal';
import Loading from '@/components/common/Loading';
import ReInviteUserModal from '@/components/userManagement/ReInviteUserModal';
import {
  useBrandUsersQuery,
  useAddUserToBrand,
} from '@/hooks/queries/useUserQueries';
import { useQueryClient } from '@tanstack/react-query';

interface ActionState {
  isOpen: boolean;
  userId: string | null;
  position: { top: number; left: number };
  userStatus: string;
  userRole: string;
  userName?: string;
  userEmail?: string;
}

const PAGE_SIZE = 6;

const UserManagement: React.FC = () => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'team' | 'roles'>('team');
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReInviteModalOpen, setIsReInviteModalOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [actionState, setActionState] = useState<ActionState>({
    isOpen: false,
    userId: null,
    position: { top: 0, left: 0 },
    userStatus: '',
    userRole: '',
    userName: '',
    userEmail: '',
  });

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, fetchUser } = userContext;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: brandUsersData,
    isLoading: isBrandUsersLoading,
    error: brandUsersError,
  } = useBrandUsersQuery(
    user?._id || '',
    user?.brand?._id || '',
    currentPage,
    PAGE_SIZE,
    debouncedSearch,
  );

  useEffect(() => {
    if (brandUsersError)
      toast.error(t('errors.fetchUsersFailed') + brandUsersError);
  }, [brandUsersError]);

  const addUserMutation = useAddUserToBrand(
    user?._id || '',
    user?.brand?._id || '',
  );

  const users = brandUsersData?.members || [];
  const paginationTotal = brandUsersData?.pagination?.total || 0;

  const isLoading = isBrandUsersLoading || isActionLoading;

  const isAdmin = useMemo(
    () => user?.brandMemberRole === BrandMemberRole.Admin,
    [user?.brandMemberRole],
  );

  const isManager = useMemo(
    () => user?.brandMemberRole === BrandMemberRole.Manager,
    [user?.brandMemberRole],
  );

  const isSuperAdmin = useMemo(
    () => user?.email === user?.brand?.email,
    [user],
  );

  const handleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const renderActionButton = (item: any) => (
    <div className="flex justify-center relative">
      <IconButton
        src="/assets/actions.svg"
        className="size-50 icon-button"
        tooltip={String(t('common.filter'))}
        onClick={(e) => handleActionToggle(e, item)}
      />
    </div>
  );

  const columns = [
    {
      title: t('userManagement.columns.name'),
      dataIndex: 'name',
      key: 'name',
      width: '100%',
      headerAlign: 'center' as const,
      cellAlign: 'center' as const,
    },
    {
      title: t('userManagement.columns.emailAddress'),
      dataIndex: 'email',
      key: 'email',
      width: '100%',
      headerAlign: 'center' as const,
      cellAlign: 'center' as const,
    },
    {
      title: t('userManagement.columns.role'),
      dataIndex: 'role',
      key: 'role',
      width: '100%',
      headerAlign: 'center' as const,
      cellAlign: 'center' as const,
      render: (item: any) => {
        const role = item.role;
        const isSuperAdmin = user?.brand?.email === item.email;
        const roleClass = isSuperAdmin
          ? 'bg-pink-200 text-pink-900'
          : role === 'admin'
            ? 'bg-[#F2EAF4] text-[#7A288A]'
            : role === 'manager'
              ? 'bg-[#FFF6F2] text-[#FFA07A]'
              : 'bg-[#F3F3F3] text-[#808080]';
        return (
          <span className={`px-2 py-1 rounded-md font-medium ${roleClass}`}>
            {isSuperAdmin
              ? t('userManagement.roles.admin')
              : role
                ? t(`userManagement.roles.${role}`) ||
                  capitalizeFirstLetter(role)
                : t('userManagement.noRole')}
          </span>
        );
      },
    },
    {
      title: t('userManagement.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: '100%',
      headerAlign: 'center' as const,
      cellAlign: 'center' as const,
      render: (item: any) => {
        const status = item.status;
        let statusClass = '';
        const displayText = status ? capitalizeFirstLetter(status) : '';

        switch (status) {
          case 'active':
            statusClass = 'bg-[#E6F8F0] text-[#00AE3E]';
            break;
          case 'invited':
            statusClass = 'bg-[#FFA5001A] bg-opacity-10 text-[#FFA500]';
            break;
          case 'deactive':
            statusClass = 'bg-[#F3F3F3] text-[#808080]';
            break;
          default:
            statusClass = 'bg-gray-100 text-gray-600';
        }

        return (
          <span className={`px-2 py-1 rounded-md font-medium ${statusClass}`}>
            {displayText}
          </span>
        );
      },
    },
    {
      title: t('userManagement.columns.invitationDate'),
      dataIndex: 'date',
      key: 'date',
      width: '100%',
      headerAlign: 'center' as const,
      cellAlign: 'center' as const,
    },
    ...(isAdmin || isManager || isSuperAdmin
      ? [
          {
            title: t('userManagement.columns.actions'),
            dataIndex: 'actions',
            key: 'actions',
            width: '100%',
            headerAlign: 'center' as const,
            cellAlign: 'center' as const,
            render: (item: any) => {
              const isCurrentUser = item.id === user?._id;
              const isTargetSuperAdmin = user?.brand?.email === item.email;
              const isPending = item.status === 'pending';

              if (isTargetSuperAdmin && isSuperAdmin) return null;

              if (isPending) {
                const canReinvite =
                  isSuperAdmin ||
                  (isAdmin &&
                    ['admin', 'manager', 'user'].includes(item.role)) ||
                  (isManager && ['manager', 'user'].includes(item.role));
                return canReinvite ? renderActionButton(item) : null;
              }

              if (isCurrentUser) {
                if (isManager) return null;
                if (isAdmin) return renderActionButton(item);
                return null;
              }

              const canManage =
                isSuperAdmin ||
                (isAdmin &&
                  (item.role === 'manager' || item.role === 'user')) ||
                (isManager && item.role === 'user');

              return canManage ? renderActionButton(item) : null;
            },
          },
        ]
      : []),
  ];

  const handleSearch = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const refreshBrandUsers = () => {
    queryClient.invalidateQueries({ queryKey: ['user', 'brandUsers'] });
  };

  const formatDataForTable = (data: any[]) => {
    return data.map((item) => ({
      id: item.userInfo?._id || '',
      name: `${item.userInfo?.firstName || ''} ${item.userInfo?.lastName || ''}`.trim(),
      email: item.userInfo?.email || '',
      role: item.brandInfo?.role || '',
      status: item.userInfo?.status || '',
      date: item.userInfo?.createdAt
        ? new Date(item.userInfo.createdAt).toLocaleDateString()
        : '',
    }));
  };

  const handleAddUser = async (userData: any) => {
    setIsActionLoading(true);
    try {
      if (user && (isAdmin || isManager)) {
        await addUserMutation.mutateAsync({
          name: userData.name,
          email: userData.email,
          role: userData.role,
        });
        toast.success(t('userManagement.userAddedSuccess'));
      }
    } catch (error: any) {
      toast.error(error.message || t('errors.addUserFailed'));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleActionToggle = useCallback((e: React.MouseEvent, item: any) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setActionState((prev) => {
      if (prev.isOpen && prev.userId === item.id) {
        return {
          isOpen: false,
          userId: null,
          position: { top: 0, left: 0 },
          userStatus: '',
          userRole: '',
          userEmail: '',
          userName: '',
        };
      }
      return {
        isOpen: true,
        userId: item.id,
        position: {
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        },
        userStatus: item.status,
        userRole: item.role,
        userEmail: item.email,
        userName: item.name,
      };
    });
  }, []);

  const memberRole =
    user?.brand?.members?.find((member) => member.userId === user._id)?.role ||
    '';

  return (
    <div>
      <AddUserModal
        isModalOpen={isModalOpen}
        handleModal={handleModal}
        onAddUser={handleAddUser}
      />
      <div className="flex flex-col p-[12px] sm:p-[24px] bg-[#F0F5FF] gap-6">
        <div className="flex flex-col xs:flex-row xs:justify-between items-start xs:items-center bg-[#F0F5FF]">
          <div className="flex items-start flex-col">
            <h3 className="text-sm justify-start text-[#6B778C} font-normal">
              {user?.brand?.name}&lsquo;s
            </h3>
            <h1 className="text-2xl font-header text-[#172B4D]">
              {t('userManagement.title')}
            </h1>
          </div>
        </div>
        <div className="flex items-center justify-between w-full xs:w-auto">
          <div className="flex items-center gap-2">
            <Button
              variant="filled"
              className={`w-full xs:w-[176px] mt-2 md:mt-0 md:ml-2 ml-0 ${
                viewMode === 'team' ? 'bg-white' : 'bg-transparent'
              } text-base font-normal`}
              onClick={() => setViewMode('team')}
            >
              {t('userManagement.teamMembers')}
            </Button>
            <Button
              variant="filled"
              className={`w-full xs:w-[200px] mt-2 md:mt-0 md:ml-2 ml-0 ${
                viewMode === 'roles' ? 'bg-white' : 'bg-transparent'
              } text-base font-normal`}
              onClick={() => setViewMode('roles')}
            >
              {t('userManagement.rolePermissions')}
            </Button>
          </div>
          {(isAdmin || isManager) && (
            <Button
              onClick={handleModal}
              className="w-full xs:w-[150px] mt-2 md:mt-0 md:ml-2 ml-0"
            >
              {t('userManagement.addUser')}
            </Button>
          )}
        </div>
      </div>

      <div className="p-[12px] sm:p-[24px]">
        {viewMode === 'team' ? (
          <>
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-2">
              <div className="border-b-2 flex items-center w-full md:w-[300px]">
                <SearchIcon className={'size-5 text-gray-400'} />
                <input
                  type="text"
                  placeholder={String(t('common.search'))}
                  name="search"
                  id="search"
                  onChange={handleSearch}
                  value={searchTerm}
                  className="border-none outline-none bg-transparent p-[10px]"
                />
              </div>
            </div>
            <Table
              columns={columns}
              dataSource={formatDataForTable(users)}
              pagination={{
                total: paginationTotal,
                current: currentPage,
                pageSize: PAGE_SIZE,
                onChange: handlePageChange,
              }}
            />
          </>
        ) : (
          <div className="bg-white rounded-3xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  role: t('userManagement.roles.user'),
                  roleKey: 'user',
                  path: '/assets/user.svg',
                  permissions: [t('userManagement.permissions.view')],
                  colors: {
                    border: 'border-[#E5E9EB]',
                    headerBg: 'bg-[#F3F3F3]',
                    permissionBorder: 'border-[#E5E9EB]',
                    text: 'text-[#808080]',
                  },
                },
                {
                  role: t('userManagement.roles.manager'),
                  roleKey: 'manager',
                  path: '/assets/briefcase.svg',
                  permissions: [
                    t('userManagement.permissions.view'),
                    t('userManagement.permissions.create'),
                    t('userManagement.permissions.edit'),
                    t('userManagement.permissions.inviteUser'),
                  ],
                  colors: {
                    border: 'border-[#FFA07A]',
                    headerBg: 'bg-[#FFF6F2]',
                    permissionBorder: 'border-[#E5E9EB]',
                    text: 'text-[#FFA07A]',
                  },
                },
                {
                  role: t('userManagement.roles.admin'),
                  roleKey: 'admin',
                  path: '/assets/shield.svg',
                  permissions: [
                    t('userManagement.permissions.view'),
                    t('userManagement.permissions.create'),
                    t('userManagement.permissions.edit'),
                    t('userManagement.permissions.delete'),
                    t('userManagement.permissions.inviteUser'),
                  ],
                  colors: {
                    border: 'border-[#873C95]',
                    headerBg: 'bg-[#F2EAF4]',
                    permissionBorder: 'border-[#E5E9EB]',
                    text: 'text-[#7A288A]',
                  },
                },
              ].map((item) => (
                <div
                  key={item.role}
                  className={`border rounded-3xl  ${memberRole && memberRole.toLowerCase() === item.roleKey ? item.colors.border : ''}`}
                >
                  <div
                    className={`flex flex-row gap-4 items-center rounded-tl-3xl rounded-tr-3xl justify-center p-4 ${item.colors.headerBg} border-b 
                    ${memberRole && memberRole.toLowerCase() === item.roleKey ? item.colors.border : ''}`}
                  >
                    <img src={item.path} className="size-50" />
                    <h3
                      className={`text-lg font-semibold ${item.colors.text} `}
                    >
                      {item.role}
                    </h3>
                  </div>
                  <div className="space-y-0 py-4">
                    {[
                      t('userManagement.permissions.view'),
                      t('userManagement.permissions.create'),
                      t('userManagement.permissions.edit'),
                      t('userManagement.permissions.delete'),
                      t('userManagement.permissions.inviteUser'),
                    ].map((permission, index, array) => (
                      <div
                        key={permission}
                        className={`flex flex-row justify-between items-center py-[6.5px] px-4
                ${index !== array.length - 1 ? `border-b ${item.colors.permissionBorder}` : ''}`}
                      >
                        <span className="ml-2 text-[#020D3A] font-normal text-sm">
                          {permission}
                        </span>
                        <img
                          src={
                            item.permissions.includes(permission)
                              ? '/assets/check.svg'
                              : '/assets/deny-x.svg'
                          }
                          className="size-50"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* {isLoading && <Loading className="z-40" />} */}
      {isLoading && (
        <Loading
          centeredToParent={true}
          style={{
            top: 'calc(50% + 60px)',
          }}
        />
      )}
      <ActionsPopup
        isOpen={actionState.isOpen}
        onClose={() => setActionState((prev) => ({ ...prev, isOpen: false }))}
        position={actionState.position}
        userStatus={actionState.userStatus}
        userRole={actionState.userRole}
        userId={actionState.userId || ''}
        currentUserRole={user?.brandMemberRole?.toLowerCase() || ''}
        setIsEditRoleModalOpen={setIsEditRoleModalOpen}
        setIsDeactivateModalOpen={setIsDeactivateModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        setIsReInviteModalOpen={setIsReInviteModalOpen}
        setIsLoading={setIsActionLoading}
        isLoading={isLoading}
      />
      <EditUserRoleModal
        isEditRoleModalOpen={isEditRoleModalOpen}
        setIsEditRoleModalOpen={setIsEditRoleModalOpen}
        userId={actionState.userId || ''}
        userName={actionState.userName || ''}
        userEmail={actionState.userEmail || ''}
        onSuccess={() => {
          refreshBrandUsers();
          fetchUser();
        }}
        setIsLoading={setIsActionLoading}
        userRole={actionState.userRole}
      />
      <DeactivateUserModel
        isDeactivateModalOpen={isDeactivateModalOpen}
        setIsDeactivateModalOpen={setIsDeactivateModalOpen}
        userStatus={actionState.userStatus}
        userName={actionState.userName || ''}
        userId={actionState.userId || ''}
        onSuccess={refreshBrandUsers}
        setIsLoading={setIsActionLoading}
      />
      <DeleteUserModal
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        userName={actionState.userName || ''}
        userId={actionState.userId || ''}
        onSuccess={refreshBrandUsers}
        setIsLoading={setIsActionLoading}
        userStatus={actionState.userStatus}
      />
      <ReInviteUserModal
        isReInviteModalOpen={isReInviteModalOpen}
        setIsReInviteModalOpen={setIsReInviteModalOpen}
        userId={user?._id || ''}
        brandId={user?.brand?._id || ''}
        invitedUserName={actionState.userName || ''}
        invitedUserEmail={actionState.userEmail || ''}
        setIsLoading={setIsActionLoading}
      />
    </div>
  );
};

export default UserManagement;
